// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { expectedIssuers, resolveTenantId } from "./entraAuthority";

export type RuntimeEnvRecord = Record<string, string | boolean | undefined>;

export const DEFAULT_AUTHORITY =
  "https://riddlecustomers.ciamlogin.com/4ce58823-87a8-4eae-baf5-388ba937b02c/v2.0";

export const DEFAULT_AUTHORITY_HOST = "riddlecustomers.ciamlogin.com";

export function getEnvValue(
  envs: RuntimeEnvRecord[],
  key: string,
): string | undefined {
  for (const env of envs) {
    const value = env[key];
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }
  return undefined;
}

export function parseApiScopes(envs: RuntimeEnvRecord[]): string[] {
  return (getEnvValue(envs, "VITE_API_SCOPES") ?? "")
    .split(/[\s,]+/)
    .map((scope) => scope.trim())
    .filter((scope) => scope.length > 0);
}

export function resolveAuthorityHost(
  authority: string,
  fallbackHost: string = DEFAULT_AUTHORITY_HOST,
): string {
  try {
    return new URL(authority).host;
  } catch {
    return fallbackHost;
  }
}

export function resolveAuthority(
  envs: RuntimeEnvRecord[],
  fallbackAuthority: string = DEFAULT_AUTHORITY,
): string {
  return getEnvValue(envs, "VITE_ENTRA_AUTHORITY") ?? fallbackAuthority;
}

export function configuredTenantId(
  envs: RuntimeEnvRecord[],
  fallbackAuthority: string = DEFAULT_AUTHORITY,
): string | undefined {
  return resolveTenantId(
    getEnvValue(envs, "VITE_ENTRA_TENANT_ID"),
    resolveAuthority(envs, fallbackAuthority),
  );
}

export function configuredTokenIssuers(
  envs: RuntimeEnvRecord[],
  fallbackAuthority: string = DEFAULT_AUTHORITY,
): string[] {
  const tenantId = configuredTenantId(envs, fallbackAuthority);
  return tenantId === undefined
    ? []
    : expectedIssuers(tenantId, resolveAuthority(envs, fallbackAuthority));
}

export function tenantMatchesAccount(
  account: { tenantId?: string },
  tenantId: string,
): boolean {
  return (account.tenantId ?? "").toLowerCase() === tenantId.toLowerCase();
}
